import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

export default function Privacy() {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    setLoaded(true);
    window.scrollTo(0, 0);
  }, []);

  const sectionStyle = {
    marginBottom: '2.5rem',
  };

  const headingStyle = {
    color: '#ffffff',
    fontSize: '1.25rem',
    fontWeight: 600,
    marginBottom: '1rem',
  };

  const paragraphStyle = {
    color: '#b8d4f1',
    fontSize: '0.95rem',
    lineHeight: 1.8,
    marginBottom: '1rem',
  };

  const listStyle = {
    color: '#a8c5e6',
    fontSize: '0.95rem',
    lineHeight: 1.8,
    marginLeft: '1.5rem',
    marginBottom: '1rem',
  };

  return (
    <div style={{
      minHeight: '100vh',
      padding: '120px 5% 80px',
    }}>
      <div style={{
        maxWidth: '800px',
        margin: '0 auto',
        opacity: loaded ? 1 : 0,
        transform: loaded ? 'translateY(0)' : 'translateY(20px)',
        transition: 'opacity 0.8s ease-out, transform 0.8s ease-out',
      }}>
        <h1 style={{
          fontSize: 'clamp(2rem, 4vw, 2.5rem)',
          fontWeight: 700,
          color: '#ffffff',
          marginBottom: '0.5rem',
        }}>
          Privacy Policy
        </h1>
        <p style={{
          color: '#6b9bc9',
          fontSize: '0.9rem',
          marginBottom: '3rem',
        }}>
          Last updated: March 30, 2026
        </p>

        <div style={{
          background: 'rgba(255, 255, 255, 0.02)',
          border: '1px solid rgba(74, 158, 255, 0.15)',
          borderRadius: '12px',
          padding: '2.5rem',
        }}>
          <section style={sectionStyle}>
            <h2 style={headingStyle}>1. Introduction</h2>
            <p style={paragraphStyle}>
              Parametrix Capital ("we," "us," or "our") is committed to protecting your privacy. 
              This Privacy Policy explains how we collect, use, disclose, and safeguard your information 
              when you use our website and subscription services.
            </p>
          </section>

          <section style={sectionStyle}>
            <h2 style={headingStyle}>2. Information We Collect</h2>
            <p style={paragraphStyle}>
              <strong>Information you provide:</strong>
            </p>
            <ul style={listStyle}>
              <li>Email address (required for subscription)</li>
              <li>Name and billing address (for payment processing)</li>
              <li>Payment information (processed securely by Stripe; we do not store card details)</li>
              <li>Communications you send to us</li>
            </ul>
            <p style={paragraphStyle}>
              <strong>Information collected automatically:</strong>
            </p>
            <ul style={listStyle}>
              <li>IP address</li>
              <li>Browser type and version</li>
              <li>Pages visited and time spent</li>
              <li>Device information</li>
            </ul>
          </section>

          <section style={sectionStyle}>
            <h2 style={headingStyle}>3. How We Use Your Information</h2>
            <p style={paragraphStyle}>
              We use your information for the following purposes:
            </p>
            <ul style={listStyle}>
              <li>To deliver our weekly Crypto Market Reports to your email</li>
              <li>To process your subscription payments</li>
              <li>To send important notices about your subscription (billing, trial ending, etc.)</li>
              <li>To respond to your inquiries and provide customer support</li>
              <li>To improve our services and website</li>
              <li>To prevent fraud and enforce our Terms of Service</li>
              <li>To watermark reports with subscriber information for anti-piracy purposes</li>
            </ul>
          </section>

          <section style={sectionStyle}>
            <h2 style={headingStyle}>4. Report Watermarking</h2>
            <p style={paragraphStyle}>
              Each report we send is personalized and contains visible and/or invisible watermarks 
              that include your email address or subscriber ID. This allows us to identify the source 
              if reports are redistributed in violation of our Terms of Service.
            </p>
          </section>

          <section style={sectionStyle}>
            <h2 style={headingStyle}>5. Third-Party Services</h2>
            <p style={paragraphStyle}>
              We use the following third-party services to operate our business:
            </p>
            <ul style={listStyle}>
              <li><strong>Stripe:</strong> For secure payment processing. Stripe's privacy policy applies to payment data.</li>
              <li><strong>Resend:</strong> For email delivery. Used to send reports and transactional emails.</li>
              <li><strong>Netlify:</strong> For website hosting.</li>
            </ul>
            <p style={paragraphStyle}>
              We do not sell, rent, or share your personal information with third parties for their 
              marketing purposes.
            </p>
          </section>

          <section style={sectionStyle}>
            <h2 style={headingStyle}>6. Data Retention</h2>
            <p style={paragraphStyle}>
              We retain your personal information for as long as your subscription is active and for 
              a reasonable period thereafter for legal and business purposes. If you cancel your 
              subscription, we may retain certain information for record-keeping and anti-fraud purposes.
            </p>
          </section>

          <section style={sectionStyle}>
            <h2 style={headingStyle}>7. Your Rights (GDPR)</h2>
            <p style={paragraphStyle}>
              If you are located in the European Economic Area or Switzerland, you have the following rights:
            </p>
            <ul style={listStyle}>
              <li><strong>Access:</strong> Request a copy of your personal data</li>
              <li><strong>Rectification:</strong> Request correction of inaccurate data</li>
              <li><strong>Erasure:</strong> Request deletion of your data (subject to legal requirements)</li>
              <li><strong>Portability:</strong> Request transfer of your data</li>
              <li><strong>Objection:</strong> Object to certain processing of your data</li>
            </ul>
            <p style={paragraphStyle}>
              To exercise these rights, please contact us through our contact page.
            </p>
          </section>

          <section style={sectionStyle}>
            <h2 style={headingStyle}>8. Data Security</h2>
            <p style={paragraphStyle}>
              We implement appropriate technical and organizational measures to protect your personal 
              information against unauthorized access, alteration, disclosure, or destruction. However, 
              no method of transmission over the Internet is 100% secure, and we cannot guarantee 
              absolute security.
            </p>
          </section>

          <section style={sectionStyle}>
            <h2 style={headingStyle}>9. Cookies</h2>
            <p style={paragraphStyle}>
              Our website may use cookies and similar technologies to enhance your experience. 
              You can control cookie settings through your browser preferences.
            </p>
          </section>

          <section style={sectionStyle}>
            <h2 style={headingStyle}>10. Children's Privacy</h2>
            <p style={paragraphStyle}>
              Our services are not intended for individuals under 18 years of age. We do not knowingly 
              collect personal information from children.
            </p>
          </section>

          <section style={sectionStyle}>
            <h2 style={headingStyle}>11. International Transfers</h2>
            <p style={paragraphStyle}>
              Your information may be transferred to and processed in countries other than your country 
              of residence. We ensure appropriate safeguards are in place for such transfers.
            </p>
          </section>

          <section style={sectionStyle}>
            <h2 style={headingStyle}>12. Changes to This Policy</h2>
            <p style={paragraphStyle}>
              We may update this Privacy Policy from time to time. We will notify you of any material 
              changes by email or by posting a notice on our website.
            </p>
          </section>

          <section style={{...sectionStyle, marginBottom: 0}}>
            <h2 style={headingStyle}>13. Contact Us</h2>
            <p style={paragraphStyle}>
              For questions about this Privacy Policy or to exercise your data rights, please contact us at{' '}
              <Link to="/contact" style={{ color: '#4a9eff', textDecoration: 'none' }}>
                our contact page
              </Link>.
            </p>
            <p style={paragraphStyle}>
              <strong>Data Controller:</strong><br />
              Parametrix Capital<br />
              Zurich, Switzerland
            </p>
          </section>
        </div>

        <div style={{
          marginTop: '2rem',
          textAlign: 'center',
        }}>
          <Link
            to="/subscription"
            style={{
              color: '#4a9eff',
              textDecoration: 'none',
              fontSize: '0.9rem',
            }}
          >
            ← Back to Subscription
          </Link>
        </div>
      </div>
    </div>
  );
}
